// Microphone controller shared by Translate and Room.
//
// Wraps audioService + websocketService the way the old voice-translation page
// did (client-side VAD filtering, pause boundaries, speaking status), adds the
// start/stop audio cues, and asks for microphone permission on first use rather
// than on page load.

import { playEarcon } from './earcons.js';

export class MicError extends Error {
    constructor(code, message) {
        super(message);
        this.code = code; // 'permission' | 'unavailable' | 'not_connected'
    }
}

export function createMicController({
    audioService,
    websocketService,
    ttsService,
    speakingThreshold = 0.02,
    onLevel = () => {},
    onPause = () => {},
    onChange = () => {},
} = {}) {
    let listening = false;
    let starting = false;
    let initialized = false;

    // Pause any translation being read aloud while the user is talking, and
    // tell the server so it can finalize the sentence promptly.
    audioService.onSpeechStart = () => {
        if (!listening) return;
        if (ttsService?.isAutoPlayEnabled()) ttsService.pausePlayback();
        websocketService.notifyUserStartedSpeaking();
    };
    audioService.onSpeechEnd = () => {
        if (!listening) return;
        if (ttsService?.isAutoPlayEnabled()) ttsService.resumePlayback();
        websocketService.notifySpeechEnded?.();
    };

    // Runs before every start, not just the first: initialize() is cheap when nothing
    // changed, and it reopens the microphone when iOS has ended the old track
    // (backgrounding, a phone call), which otherwise records silence until reload.
    async function ensureAudio() {
        if (!navigator.mediaDevices?.getUserMedia) {
            throw new MicError('unavailable', 'This browser cannot use the microphone.');
        }
        try {
            await audioService.initialize();
            initialized = true;
        } catch (error) {
            const denied = /denied|NotAllowed|Permission/i.test(`${error?.name} ${error?.message}`);
            throw new MicError(
                denied ? 'permission' : 'unavailable',
                denied ? 'Microphone access is blocked.' : 'The microphone could not be started.'
            );
        }
    }

    async function start() {
        if (listening || starting) return;
        if (!websocketService.isConnected() || !websocketService.getCurrentParticipantId()) {
            throw new MicError('not_connected', 'Still connecting to the translation service.');
        }
        starting = true;
        try {
            await ensureAudio();

            // Play the cue before opening the mic so it isn't captured.
            await playEarcon('start');

            const serverInterval = websocketService?.serverConfig?.audioChunkIntervalMs ?? 100;
            const chunkIntervalMs = Math.max(serverInterval, audioService.getMinChunkIntervalMs());

            audioService.startRecording(
                (audioData, audioFormat) => {
                    // Only send chunks with voice in them; cuts traffic and backend load.
                    if (websocketService.isConnected() && audioService.hasVoiceActivity()) {
                        websocketService.sendAudioChunk(audioData, false, audioFormat);
                    }
                },
                (volume) => {
                    onLevel(volume);
                    if (websocketService.isConnected()) {
                        websocketService.setSpeakingStatus(volume > speakingThreshold);
                    }
                },
                (pauseDuration) => {
                    audioService.sendAudioOnPause();
                    onPause(pauseDuration);
                },
                chunkIntervalMs
            );

            if (!audioService.isCalibrated) {
                audioService.calibrateNoiseFloor(1000);
            }

            listening = true;
            onChange(true);
        } catch (error) {
            playEarcon('error');
            throw error instanceof MicError ? error : new MicError('unavailable', error?.message || 'Microphone error');
        } finally {
            starting = false;
        }
    }

    function stop({ cue = true } = {}) {
        if (!listening) return;
        try {
            audioService.sendAudioOnPause();
        } catch (error) {
            console.warn('Mic: sendAudioOnPause failed', error);
        }
        if (websocketService.isConnected()) {
            // An explicit pause boundary makes the server finalize the sentence now.
            websocketService.sendAudioChunk(new Uint8Array(1920), true);
        }
        try {
            audioService.stopRecording();
        } catch (error) {
            console.warn('Mic: stopRecording failed', error);
        }
        listening = false;
        if (websocketService.isConnected()) {
            websocketService.setSpeakingStatus(false);
        }
        onChange(false);
        if (cue) playEarcon('stop');
    }

    return {
        start,
        stop,
        toggle() {
            return listening ? stop() : start();
        },
        get isListening() {
            return listening;
        },
        get isStarting() {
            return starting;
        },
        cleanup() {
            stop({ cue: false });
            audioService.cleanup?.();
            initialized = false;
        },
    };
}

/** Plain-language text for a MicError. */
export function micErrorMessage(error) {
    switch (error?.code) {
        case 'permission':
            return 'Microphone access is blocked. Allow it in your browser\'s site settings, then try again.';
        case 'not_connected':
            return 'Still connecting to the translation service. Try again in a moment.';
        default:
            return error?.message || 'The microphone could not be started.';
    }
}
